import React from "react";
import { Box, Container, Heading, Text, VStack, HStack, SimpleGrid, Button, List, ListItem, ListIcon } from "@chakra-ui/react";
import { FaArrowRight, FaCheckCircle, FaClock, FaArrowDown, FaBolt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const SolutionsRefinanceSolarBattery: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solutions/refinance-solar-battery"
        fallbackTitle="Refinance Solar + Battery Solutions"
        fallbackDescription="Unlock equity and finance solar upgrades through smart refinancing"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Heading size="2xl" fontWeight="bold">
              Refinance + Solar & Battery Package
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">
              Refinance your home and add solar in one transaction
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Before & After Scenario Cards */}
      <Container maxW="container.xl" py={16}>
        <Heading size="lg" mb={12} textAlign="center">
          Transform Your Finances in One Smart Move
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          {/* Current Situation Card */}
          <Box bg="gray.50" p={8} borderRadius="2xl" border="2px dashed" borderColor="gray.300" position="relative">
            <Box position="absolute" top="-16px" left="50%" transform="translateX(-50%)" bg="gray.500" color="white" px={4} py={1} borderRadius="full" fontSize="sm" fontWeight="bold">
              Current Situation
            </Box>
            <VStack align="start" spacing={4} mt={4}>
              <Text fontSize="sm" color="gray.600" fontWeight="bold" textTransform="uppercase">Your Current Setup</Text>

              <Box w="100%">
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="medium">Old Mortgage Rate:</Text>
                  <Text fontWeight="bold" color="gray.800">5.8%</Text>
                </HStack>
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="medium">Monthly Payment:</Text>
                  <Text fontWeight="bold">$3,200</Text>
                </HStack>
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="medium">Electricity Bill:</Text>
                  <Text fontWeight="bold">$380/month</Text>
                </HStack>
                <Box h="1px" bg="gray.300" my={3} />
                <HStack justify="space-between">
                  <Text fontWeight="bold" fontSize="lg">Total Monthly Cost:</Text>
                  <Text fontWeight="bold" fontSize="lg" color="gray.800">$3,580</Text>
                </HStack>
              </Box>

              <Box bg="gray.100" p={4} borderRadius="lg" w="100%">
                <Text fontSize="sm" fontWeight="bold" mb={2}>Current Challenges:</Text>
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm">• Paying above-market interest rate</Text>
                  <Text fontSize="sm">• Rising electricity costs</Text>
                  <Text fontSize="sm">• No access to home equity</Text>
                  <Text fontSize="sm">• Missing out on solar benefits</Text>
                </VStack>
              </Box>
            </VStack>
          </Box>

          {/* After Refinance + Solar Card */}
          <Box bg="white" p={8} borderRadius="2xl" border="3px solid" borderColor="#C2A878" position="relative" boxShadow="xl">
            <Box position="absolute" top="-16px" left="50%" transform="translateX(-50%)" bg="#C2A878" color={brandConfig.colors.primary} px={4} py={1} borderRadius="full" fontSize="sm" fontWeight="bold">
              After Refinance + Solar
            </Box>
            <VStack align="start" spacing={4} mt={4}>
              <Text fontSize="sm" color="gray.600" fontWeight="bold" textTransform="uppercase">Your New Setup</Text>

              <Box w="100%">
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="medium">New Mortgage Rate:</Text>
                  <Text fontWeight="bold" color={brandConfig.colors.primary}>4.9%</Text>
                </HStack>
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="medium">Monthly Payment:</Text>
                  <Text fontWeight="bold">$3,100</Text>
                </HStack>
                <HStack justify="space-between" mb={2}>
                  <Text fontWeight="medium">Electricity Bill:</Text>
                  <Text fontWeight="bold" color={brandConfig.colors.primary}>$0/month</Text>
                </HStack>
                <Box h="1px" bg="#C2A878" my={3} />
                <HStack justify="space-between">
                  <Text fontWeight="bold" fontSize="lg">Total Monthly Cost:</Text>
                  <Text fontWeight="bold" fontSize="lg" color={brandConfig.colors.primary}>$3,100</Text>
                </HStack>
              </Box>

              <Box bg="white" p={4} borderRadius="lg" w="100%">
                <Text fontSize="sm" fontWeight="bold" mb={2} color={brandConfig.colors.primary}>New Benefits:</Text>
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm">✓ Better interest rate saves money</Text>
                  <Text fontSize="sm">✓ Zero electricity bills</Text>
                  <Text fontSize="sm">✓ Solar + battery fully covered</Text>
                  <Text fontSize="sm">✓ Increased home value</Text>
                </VStack>
              </Box>
            </VStack>
          </Box>
        </SimpleGrid>

        {/* Arrow and Savings Highlight */}
        <Box textAlign="center" mt={8}>
          <VStack spacing={2}>
            <Box as={FaArrowRight} size="32px" color={brandConfig.colors.primary} transform="rotate(90deg)" />
            <Box bg="white" border="2px solid" borderColor="#C2A878" p={4} borderRadius="lg" display="inline-block">
              <Text fontSize="sm" fontWeight="bold" color="gray.700">NET MONTHLY SAVINGS</Text>
              <Text fontSize="4xl" fontWeight="bold" color={brandConfig.colors.primary}>$480</Text>
              <Text fontSize="sm" color="gray.600">That's $5,760 per year back in your pocket!</Text>
            </Box>
          </VStack>
        </Box>
      </Container>

      {/* Savings Breakdown */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <Heading size="lg" mb={12} textAlign="center">
            How Your Savings Break Down
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <Box bg="white" p={6} borderRadius="lg" boxShadow="md" textAlign="center">
              <Box as={FaArrowDown} size="40px" color={brandConfig.colors.primary} mx="auto" mb={4} />
              <Text fontSize="3xl" fontWeight="bold" color={brandConfig.colors.primary}>$100/mo</Text>
              <Text fontWeight="bold" mb={2}>Lower Interest Rate</Text>
              <Text fontSize="sm" color="gray.600">Refinancing from 5.8% to 4.9% reduces your monthly mortgage payment</Text>
            </Box>

            <Box bg="white" p={6} borderRadius="lg" boxShadow="md" textAlign="center">
              <Box as={FaBolt} size="40px" color={brandConfig.colors.primary} mx="auto" mb={4} />
              <Text fontSize="3xl" fontWeight="bold" color={brandConfig.colors.primary}>$380/mo</Text>
              <Text fontWeight="bold" mb={2}>Zero Electricity Bills</Text>
              <Text fontSize="sm" color="gray.600">Solar + battery eliminates your entire electricity cost</Text>
            </Box>

            <Box bg="white" p={6} borderRadius="lg" boxShadow="md" textAlign="center">
              <Box as={FaCheckCircle} size="40px" color={brandConfig.colors.primary} mx="auto" mb={4} />
              <Text fontSize="3xl" fontWeight="bold" color={brandConfig.colors.primary}>$0</Text>
              <Text fontWeight="bold" mb={2}>Out-of-Pocket Cost</Text>
              <Text fontSize="sm" color="gray.600">Solar system fully financed within your new mortgage</Text>
            </Box>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Why Now? Section (3 reasons with icons) */}
      <Container maxW="container.xl" py={16}>
        <Heading size="lg" mb={12} textAlign="center">
          Why Refinance + Solar Makes Sense Right Now
        </Heading>
        <VStack spacing={6} align="stretch" maxW="3xl" mx="auto">
          <HStack align="start" bg="gray.50" p={6} borderRadius="lg" spacing={4} borderLeft="4px solid" borderColor={brandConfig.colors.primary}>
            <Box as={FaArrowDown} size="32px" color={brandConfig.colors.primary} flexShrink={0} mt={1} />
            <VStack align="start" spacing={2}>
              <Heading size="md">Interest Rates Are Competitive</Heading>
              <Text color="gray.700">
                Current mortgage rates are favorable for refinancing. Lock in a better rate while accessing equity for solar installation without impacting your monthly budget.
              </Text>
            </VStack>
          </HStack>

          <HStack align="start" bg="gray.50" p={6} borderRadius="lg" spacing={4} borderLeft="4px solid" borderColor={brandConfig.colors.primary}>
            <Box as={FaBolt} size="32px" color={brandConfig.colors.primary} flexShrink={0} mt={1} />
            <VStack align="start" spacing={2}>
              <Heading size="md">Electricity Prices Keep Rising</Heading>
              <Text color="gray.700">
                Australian electricity prices have increased 120% in the last decade. Solar and battery storage protect you from future price hikes while providing energy independence.
              </Text>
            </VStack>
          </HStack>

          <HStack align="start" bg="gray.50" p={6} borderRadius="lg" spacing={4} borderLeft="4px solid" borderColor={brandConfig.colors.primary}>
            <Box as={FaClock} size="32px" color={brandConfig.colors.primary} flexShrink={0} mt={1} />
            <VStack align="start" spacing={2}>
              <Heading size="md">Solar Incentives Still Available</Heading>
              <Text color="gray.700">
                Government rebates and Small-scale Technology Certificates (STCs) reduce the upfront cost of solar by thousands. These incentives decrease over time, so acting now maximizes your savings.
              </Text>
            </VStack>
          </HStack>
        </VStack>
      </Container>

      {/* Process Timeline (4 stages) */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <Heading size="lg" mb={12} textAlign="center">
            Simple 4-Stage Process
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            <VStack bg="white" p={6} borderRadius="lg" boxShadow="sm" spacing={4} position="relative">
              <Box position="absolute" top="-16px" bg={brandConfig.colors.primary} color="white" w="32px" h="32px" borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontWeight="bold">
                1
              </Box>
              <Heading size="sm" mt={4}>Assessment</Heading>
              <List spacing={2} fontSize="sm" color="gray.600">
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Mortgage review
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Solar site evaluation
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Savings calculation
                </ListItem>
              </List>
            </VStack>

            <VStack bg="white" p={6} borderRadius="lg" boxShadow="sm" spacing={4} position="relative">
              <Box position="absolute" top="-16px" bg={brandConfig.colors.primary} color="white" w="32px" h="32px" borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontWeight="bold">
                2
              </Box>
              <Heading size="sm" mt={4}>Approval</Heading>
              <List spacing={2} fontSize="sm" color="gray.600">
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Refinance application
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Solar system design
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Final loan approval
                </ListItem>
              </List>
            </VStack>

            <VStack bg="white" p={6} borderRadius="lg" boxShadow="sm" spacing={4} position="relative">
              <Box position="absolute" top="-16px" bg={brandConfig.colors.primary} color="white" w="32px" h="32px" borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontWeight="bold">
                3
              </Box>
              <Heading size="sm" mt={4}>Settlement</Heading>
              <List spacing={2} fontSize="sm" color="gray.600">
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Refinance settles
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Solar installation begins
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  One streamlined process
                </ListItem>
              </List>
            </VStack>

            <VStack bg="white" p={6} borderRadius="lg" boxShadow="sm" spacing={4} position="relative">
              <Box position="absolute" top="-16px" bg={brandConfig.colors.primary} color="white" w="32px" h="32px" borderRadius="full" display="flex" alignItems="center" justifyContent="center" fontWeight="bold">
                4
              </Box>
              <Heading size="sm" mt={4}>Enjoy Savings</Heading>
              <List spacing={2} fontSize="sm" color="gray.600">
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Lower mortgage payment
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Zero electricity bills
                </ListItem>
                <ListItem>
                  <ListIcon as={FaCheckCircle} color={brandConfig.colors.primary} />
                  Immediate savings
                </ListItem>
              </List>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container maxW="container.xl" py={16}>
        <Box bg={brandConfig.colors.primary} p={12} borderRadius="2xl" textAlign="center" color="white">
          <Heading size="xl" mb={4}>Ready to Lower Your Bills and Go Solar?</Heading>
          <Text fontSize="lg" mb={8} opacity={0.9} maxW="2xl" mx="auto">
            Book a free refinance + solar assessment. We'll show you exactly how much you can save each month and help you navigate the entire process from start to finish.
          </Text>
          <HStack spacing={4} justify="center" flexWrap="wrap">
            <Button size="lg" bg="white" color={brandConfig.colors.primary} _hover={{ bg: "gray.100" }} onClick={() => navigate("/contact")}>
              Get Free Assessment
            </Button>
            <Button size="lg" variant="outline" colorScheme="whiteAlpha" borderColor="white" color="white" _hover={{ bg: "whiteAlpha.200" }} onClick={() => navigate("/contact")}>
              Speak to a Specialist
            </Button>
          </HStack>
        </Box>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default SolutionsRefinanceSolarBattery;
